import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ProductDetail } from '../types/product';
import { cartAPI } from '../api/cart';
import { productsAPI } from '../api/products';
import { sessionStorage, setupAutoSave } from '../utils/sessionStorage';
import { getErrorDetail } from '../utils/errorDetail';

// ─── CC Cart (Custom Cakes) ────────────────────────────────────────────────

// A single chosen CakeOptionItem, carried on a cart line for customizable
// (non-BYO) products. Mirrors CakeOptionItem from api/byo.ts but keeps the
// camelCase shape already used by BYOCartItem.selectedOptions below.
export interface SelectedOption {
  itemId: number;
  categoryId: number;
  name: string;
  name_zh?: string | null;
  priceModifier: number;
}

// A single chosen Cake Option item (cc_* tables, task-191) carried on a cart
// line. Display data only: `price` is the effective price the customer saw;
// the server recomputes every price from itemId at cart/checkout time.
export interface CCSelectedOption {
  groupId: number;
  groupName: string;
  groupName_zh?: string | null;
  itemId: number;
  name: string;
  name_zh?: string | null;
  price: number;
}

interface CartItem {
  product: ProductDetail;
  quantity: number;
  selectedSize?: string;
  sizePrice?: number; // Price with size modifier applied
  cartItemId?: number; // For backend sync (reserved — not currently populated)
  selectedOptions?: SelectedOption[]; // Customization selections (non-BYO products)
  ccOptions?: CCSelectedOption[]; // Cake Option selections (cc_* groups); part of line identity
  customImageUrl?: string; // App-issued cake photo URL (customizable products); part of line identity
  // Stable synthetic row identity, assigned ONCE when the row is created
  // (addToCart) and never recomputed afterwards. Rows are matched by this
  // id — never by array index and never by re-deriving from current
  // product/size/options — so a row keeps its identity across quantity
  // changes, size edits, and array-shifting removals of OTHER rows. See
  // makeRowId() below.
  rowId: string;
}

interface CCCartContextType {
  items: CartItem[];
  addToCart: (product: ProductDetail, quantity?: number, selectedSize?: string, sizePrice?: number, selectedOptions?: SelectedOption[], customImageUrl?: string, ccOptions?: CCSelectedOption[]) => void;
  removeFromCart: (productId: number, selectedSize?: string, selectedOptions?: SelectedOption[], customImageUrl?: string, ccOptions?: CCSelectedOption[]) => void;
  // `rowId` is the target row's stable CartItem.rowId (see CartItem) — NOT
  // an array index and NOT product_id. product_id alone is ambiguous when a
  // product appears in multiple rows with different size/options, and an
  // index is unsafe because it can shift between when the caller captures
  // it and when this actually runs (e.g. behind a setTimeout).
  updateQuantity: (rowId: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartCount: () => number;
  isInCart: (productId: number) => boolean;
  isLoading: boolean;
  sessionId: string | null;
  cartWarning: string | null;
  dismissCartWarning: () => void;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  // Both resolve their target row by CartItem.rowId, inside the setItems
  // functional updater, so they always operate on current state rather
  // than a position captured earlier (see rowId doc on CartItem).
  replaceCCItem: (rowId: string, updatedItem: CartItem) => void;
  removeCCItem: (rowId: string) => void;
  // Re-fetches has_selectable_options/has_required_options for the products in
  // the cart and merges ONLY those two flags into the rows (stale snapshots).
  refreshCartProductFlags: () => Promise<void>;
  byoItems: BYOCartItem[];
  addBYOItem: (item: BYOCartItem) => void;
  removeBYOItem: (byoId: string) => void;
  clearBYOItems: () => void;
  getByoTotal: () => number;
}

const CCCartContext = createContext<CCCartContextType | undefined>(undefined);

// ─── BYO Cart (Build Your Own Cake) ──────────────────────────────────────

export interface BYOCartItem {
  type: 'byo';
  byoId: string;
  name: string;
  basePrice: number;
  selectedOptions: Array<{
    itemId: number;
    name: string;
    name_zh?: string | null;
    imageUrl: string | null;
    priceModifier: number;
  }>;
  totalPrice: number;
  quantity: 1;
}

// ─── QB Cart (Quick Bites & Drinks) ───────────────────────────────────────

export interface QBCartItem {
  type: 'bubble_tea' | 'instant_cake';
  itemId: number;
  name: string;
  price: number;
  quantity: number;
  options?: Record<string, string>;
}

interface QBCartContextType {
  qbItems: QBCartItem[];
  addQBItem: (item: Omit<QBCartItem, 'quantity'> & { quantity?: number }) => void;
  removeQBItem: (itemId: number, type: QBCartItem['type']) => void;
  updateQBQuantity: (itemId: number, type: QBCartItem['type'], quantity: number) => void;
  clearQBCart: () => void;
  getQBCartTotal: () => number;
  qbCartCount: number;
  replaceQBItemAtIndex: (index: number, newItem: QBCartItem) => void;
}

const QBCartContext = createContext<QBCartContextType | undefined>(undefined);

// ─── Hooks ─────────────────────────────────────────────────────────────────

export const useCCCart = () => {
  const context = useContext(CCCartContext);
  if (!context) {
    throw new Error('useCCCart must be used within a CartProvider');
  }
  return context;
};

/** Alias for useCCCart — maintains full backward compatibility */
export const useCart = () => {
  const context = useContext(CCCartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const useQBCart = () => {
  const context = useContext(QBCartContext);
  if (!context) {
    throw new Error('useQBCart must be used within a CartProvider');
  }
  return context;
};

// ─── Helpers ───────────────────────────────────────────────────────────────

// Canonical 8-4-4-4-12 hex UUID (the backend rejects any other session_id with 400).
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Generates an RFC4122 v4 UUID; falls back when crypto.randomUUID is unavailable. */
export const generateUuid = (): string => {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// Returning users with a legacy `session_<ts>_<rand>` id are silently migrated to a UUID.
export const getOrCreateSessionId = (): string => {
  let sessionId = localStorage.getItem('cart_session_id');
  if (!sessionId || !UUID_REGEX.test(sessionId)) {
    sessionId = generateUuid();
    localStorage.setItem('cart_session_id', sessionId);
  }
  return sessionId;
};

const loadQBCartFromStorage = (): QBCartItem[] => {
  try {
    const raw = localStorage.getItem('qb_cart');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed as QBCartItem[];
      }
    }
  } catch {}
  return [];
};

const saveQBCartToStorage = (items: QBCartItem[]) => {
  localStorage.setItem('qb_cart', JSON.stringify(items));
};

const loadBYOCartFromStorage = (): BYOCartItem[] => {
  try {
    const raw = localStorage.getItem('byo_cart');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed as BYOCartItem[];
      }
    }
  } catch {}
  return [];
};

const saveBYOCartToStorage = (items: BYOCartItem[]) => {
  localStorage.setItem('byo_cart', JSON.stringify(items));
};

// Builds a stable identity key for a set of selected options, keyed by
// CakeOptionItem.id (never by joined/split option names — that class of bug
// has shipped twice already in this codebase's BT/QB carts). Sorted so that
// selection order doesn't affect identity, and deliberately NOT
// JSON.stringify(options) since object key order is not guaranteed stable.
const selectionsKey = (options?: SelectedOption[]): string => {
  if (!options || options.length === 0) return '';
  return options
    .map(o => o.itemId)
    .sort((a, b) => a - b)
    .join(',');
};

// Builds the stable per-row identity used for CartItem.rowId. Reuses
// addToCart's own merge key (product_id + selectedSize + selectionsKey),
// which is already guaranteed unique across `items` at the moment a row is
// created — addToCart merges any new addition matching an existing row's
// merge key into that row instead of pushing a second row. Assigned ONCE
// per row (see addToCart) and never recomputed, so it stays valid identity
// even after a later size edit changes selectedSize/sizePrice on that row.
// The `::img:<url>` suffix is appended ONLY when a cake photo is set, so
// photo-less rows (including carts persisted by older builds) keep exactly
// the keys they had before this field existed.
// Same idea for Cake Option (cc_*) selections, keyed by cc_option_items.id. The
// `::cc:<ids>` suffix is appended ONLY when cc options are selected, so rows
// persisted by older builds keep exactly the keys they had before.
const ccKey = (options?: CCSelectedOption[]): string => {
  if (!options || options.length === 0) return '';
  return options
    .map(o => o.itemId)
    .sort((a, b) => a - b)
    .join(',');
};

const makeRowId = (productId: number, selectedSize?: string, selectedOptions?: SelectedOption[], customImageUrl?: string, ccOptions?: CCSelectedOption[]): string =>
  `${productId}::${selectedSize || ''}::${selectionsKey(selectedOptions)}${ccOptions && ccOptions.length > 0 ? `::cc:${ccKey(ccOptions)}` : ''}${customImageUrl ? `::img:${customImageUrl}` : ''}`;

const sameImage = (a?: string, b?: string): boolean => (a || '') === (b || '');

// Backfills rowId onto CartItem rows loaded from localStorage/session
// storage that predate this field (persisted by an older build), so
// existing customers' in-flight carts don't end up with rows that fail
// every rowId-based lookup after this change ships.
const withRowIds = (rawItems: any[]): CartItem[] =>
  rawItems.map(item => ({
    ...item,
    rowId: typeof item.rowId === 'string' && item.rowId
      ? item.rowId
      : makeRowId(item.product?.product_id, item.selectedSize, item.selectedOptions, item.customImageUrl, item.ccOptions),
  }));

// ─── Provider ──────────────────────────────────────────────────────────────

interface CartProviderProps {
  children: React.ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // ── CC Cart state ──
  const [items, setItems] = useState<CartItem[]>(() => {
    // Load cart from enhanced session storage
    const storedCart = sessionStorage.loadCart();
    if (storedCart && storedCart.items) {
      return withRowIds(storedCart.items);
    }
    // Fallback to old localStorage format
    const oldCart = localStorage.getItem('cart');
    if (oldCart) {
      try {
        const parsed = JSON.parse(oldCart);
        if (Array.isArray(parsed)) {
          return withRowIds(parsed);
        }
      } catch {}
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => getOrCreateSessionId());
  const [syncError, setSyncError] = useState(false);
  const [cartWarning, setCartWarning] = useState<string | null>(null);

  const dismissCartWarning = React.useCallback(() => {
    setCartWarning(null);
  }, []);

  const [isCartOpen, setIsCartOpen] = useState(false);

  const openCart = React.useCallback(() => {
    setIsCartOpen(true);
  }, []);

  const closeCart = React.useCallback(() => {
    setIsCartOpen(false);
  }, []);

  // Save CC cart to enhanced session storage whenever it changes
  useEffect(() => {
    sessionStorage.saveCart(items, sessionId);
  }, [items, sessionId]);

  // Setup auto-save to extend session
  useEffect(() => {
    const cleanup = setupAutoSave(() => items, sessionId);
    return cleanup;
  }, [items, sessionId]);

  // Load CC cart from backend on mount
  useEffect(() => {
    let isMounted = true;

    const loadCartFromBackend = async () => {
      if (syncError) return;

      try {
        setIsLoading(true);
        const cartResponse = await cartAPI.getCart(sessionId);

        if (!isMounted) return;

        if (cartResponse.items && cartResponse.items.length > 0) {
          console.log('Cart loaded from backend:', cartResponse);
        }
      } catch (error) {
        if (!isMounted) return;
        console.log('Using local storage for cart (backend unavailable)');
        setSyncError(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCartFromBackend();

    return () => {
      isMounted = false;
    };
  }, [sessionId, syncError]);

  // In-flight addToCart calls, keyed by a per-call id (NOT the merge key,
  // which is deterministic and shared by remove-then-re-add). An entry means
  // "this call's optimistic contribution is still sitting in its row". Refs
  // only — no CartItem fields, no persistence change.
  const pendingAddsRef = useRef(new Map<string, { key: string; qty: number }>());
  const nextCallIdRef = useRef(0);

  // Idempotent (safe under StrictMode double-invoked updaters). Drops pending
  // entries for a merge key whose row was removed, so their late rollbacks
  // cannot subtract from a re-created row. `cutoff` limits it to calls that
  // were already issued when the removal was requested, so a same-batch
  // re-add (registered before the updater runs) is left alone.
  const invalidateKey = React.useCallback((key: string, cutoff: number) => {
    pendingAddsRef.current.forEach((entry, id) => {
      if (entry.key === key && Number(id) <= cutoff) pendingAddsRef.current.delete(id);
    });
  }, []);

  const invalidateAll = React.useCallback((cutoff: number) => {
    pendingAddsRef.current.forEach((_, id) => {
      if (Number(id) <= cutoff) pendingAddsRef.current.delete(id);
    });
  }, []);

  const addToCart = React.useCallback(async (product: ProductDetail, quantity: number = 1, selectedSize?: string, sizePrice?: number, selectedOptions?: SelectedOption[], customImageUrl?: string, ccOptions?: CCSelectedOption[]) => {
    const callId = String(++nextCallIdRef.current);
    const callKey = makeRowId(product.product_id, selectedSize, selectedOptions, customImageUrl, ccOptions);
    pendingAddsRef.current.set(callId, { key: callKey, qty: quantity });

    setItems(currentItems => {
      const newItems = [...currentItems];

      const existingItemIndex = newItems.findIndex(
        item => item.product.product_id === product.product_id &&
                item.selectedSize === selectedSize &&
                selectionsKey(item.selectedOptions) === selectionsKey(selectedOptions) &&
                ccKey(item.ccOptions) === ccKey(ccOptions) &&
                sameImage(item.customImageUrl, customImageUrl)
      );

      if (existingItemIndex > -1) {
        // Merging into an existing row — keep its rowId untouched so any
        // in-flight action (swipe delete, edit modal) targeting that row by
        // rowId is unaffected by this merge.
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + quantity
        };
      } else {
        newItems.push({
          product,
          quantity,
          selectedSize,
          sizePrice,
          selectedOptions,
          ...(ccOptions && ccOptions.length > 0 ? { ccOptions } : {}),
          ...(customImageUrl ? { customImageUrl } : {}),
          rowId: makeRowId(product.product_id, selectedSize, selectedOptions, customImageUrl, ccOptions),
        });
      }

      return newItems;
    });

    try {
      await cartAPI.addItem({
        product_id: product.product_id,
        quantity,
        size: selectedSize,
        selected_cc_option_ids: ccOptions?.map(o => o.itemId) ?? [],
      }, sessionId);

      // A successful sync means the backend is reachable again — self-heal
      // any earlier-session syncError so future addToCart calls keep
      // attempting to sync instead of being permanently stuck in
      // local-storage-only mode for the rest of the session. This reset is
      // now unconditional (not gated by `if (syncError)`): when two
      // addToCart calls overlap and share the same function instance, each
      // closes over the same syncError snapshot from when they fired, so a
      // guard here can read a stale `false` even after the sibling call has
      // since set the real value to `true`, silently skipping the reset.
      // Calling setSyncError(false) unconditionally sidesteps that stale
      // closure entirely — it's a no-op if it was already false.
      setSyncError(false);
      pendingAddsRef.current.delete(callId);
    } catch (error: any) {
      console.log('Failed to sync cart with backend:', error);

      // A cancelled request (e.g. the browser aborted it — component
      // unmount, a superseding request, navigation away) says nothing
      // about whether the item was actually added or whether the backend
      // is reachable at all. Peel this case off FIRST, before the
      // 404/400/no-response branch below: no rollback (the optimistic add
      // stays exactly as it is), no cartWarning, no forcing the cart
      // drawer open, no syncError. Must stay a pure carve-out — the
      // 404/400/no-response logic below is intentionally left untouched.
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        pendingAddsRef.current.delete(callId);
        return;
      }

      if (error?.response) {
        // The backend received the request and returned a definite answer
        // — 404/400 ("this item's size/price is invalid"), but also
        // 422/429/500/503/anything else the backend now or later returns.
        // Whatever the status, we know FOR CERTAIN the item was not
        // durably added the way the optimistic update assumed. Roll back
        // just this call's own contribution by subtracting its own
        // `quantity` from the row's live state (via the functional updater).
        // This stays correct however many other calls for the same
        // product+size overlapped or in what order they resolved. If the row
        // was removed/cleared/replaced after this call fired, its
        // contribution is already gone (pendingAddsRef entry invalidated), so
        // the rollback is skipped — otherwise it would subtract from a row
        // re-created by a later add. Liveness is decided once (closure var)
        // so a StrictMode double-invoked updater gives the same answer.
        let live: boolean | undefined;
        // Read synchronously (never inside the updater): calls issued up to
        // now are invalidated if this rollback removes the row.
        const rollbackCutoff = nextCallIdRef.current;
        setItems(currentItems => {
          if (live === undefined) live = pendingAddsRef.current.has(callId);
          pendingAddsRef.current.delete(callId);
          if (!live) return currentItems;

          const idx = currentItems.findIndex(
            item => item.product.product_id === product.product_id &&
                    item.selectedSize === selectedSize &&
                    selectionsKey(item.selectedOptions) === selectionsKey(selectedOptions) &&
                    ccKey(item.ccOptions) === ccKey(ccOptions) &&
                    sameImage(item.customImageUrl, customImageUrl)
          );
          if (idx === -1) return currentItems;

          const newQuantity = currentItems[idx].quantity - quantity;
          if (newQuantity <= 0) {
            // Defensive fallback (explicit edits already invalidate this
            // call). Row is removed, so all pending adds for the key issued
            // so far are moot — not just older ones.
            invalidateKey(callKey, rollbackCutoff);
            return currentItems.filter((_, i) => i !== idx);
          }
          const updated = [...currentItems];
          updated[idx] = { ...updated[idx], quantity: newQuantity };
          return updated;
        });

        setCartWarning(prev => prev ?? (getErrorDetail(error) || "Sorry, that item could not be added to your cart."));
        setIsCartOpen(true);
      } else {
        // No `.response` at all (network failure, timeout, connection
        // refused, CORS block) and NOT a cancellation (already peeled off
        // above by Fix #4) — genuinely ambiguous whether the item reached
        // the backend, unlike a real response-bearing rejection. Do NOT
        // roll back the optimistic add here: `syncError`/
        // `loadCartFromBackend` is only a one-time reachability ping, not
        // real per-item server reconciliation (see frontend.md), so
        // rolling back on a guess risks silently dropping an item that
        // actually was added server-side. Instead, flip `syncError` so
        // this session stops attempting further backend syncs until a
        // later addToCart call succeeds again (self-healing — see the
        // success branch above), while still surfacing the existing
        // warning and opening the drawer — the item the customer sees
        // really is what's in local state, since nothing was rolled back.
        pendingAddsRef.current.delete(callId);
        setSyncError(true);
        setCartWarning(prev => prev ?? (getErrorDetail(error) || "We couldn't confirm this item was added to your cart. Please check your cart before checking out."));
        setIsCartOpen(true);
      }
    }
    // syncError intentionally omitted — the body no longer reads it (the
    // success-path reset above is now unconditional), so including it would
    // just churn this callback's identity every time syncError flips.
  }, [sessionId, invalidateKey]);

  // Matches addToCart's merge key (product_id + selectedSize +
  // selectionsKey(selectedOptions)) exactly, so this removes only the row(s)
  // matching that full identity rather than every row sharing the same
  // product_id.
  const removeFromCart = (productId: number, selectedSize?: string, selectedOptions?: SelectedOption[], customImageUrl?: string, ccOptions?: CCSelectedOption[]) => {
    const cutoff = nextCallIdRef.current;
    setItems(currentItems => {
      let removed = false;
      const next = currentItems.filter(item => {
        const match = item.product.product_id === productId &&
          item.selectedSize === selectedSize &&
          selectionsKey(item.selectedOptions) === selectionsKey(selectedOptions) &&
          ccKey(item.ccOptions) === ccKey(ccOptions) &&
          sameImage(item.customImageUrl, customImageUrl);
        if (match) {
          removed = true;
          invalidateKey(makeRowId(item.product.product_id, item.selectedSize, item.selectedOptions, item.customImageUrl, item.ccOptions), cutoff);
        }
        return !match;
      });
      return removed ? next : currentItems;
    });
  };

  // rowId-based — resolves the target row's CURRENT position inside the
  // setItems functional updater (never a position captured earlier by the
  // caller), so a pending/delayed call for one row is unaffected by other
  // rows being added/removed/reordered in the meantime.
  const updateQuantity = (rowId: string, quantity: number) => {
    const cutoff = nextCallIdRef.current;
    setItems(currentItems => {
      const index = currentItems.findIndex(item => item.rowId === rowId);
      if (index === -1) return currentItems;
      if (quantity <= 0) {
        const gone = currentItems[index];
        invalidateKey(makeRowId(gone.product.product_id, gone.selectedSize, gone.selectedOptions, gone.customImageUrl, gone.ccOptions), cutoff);
        return currentItems.filter((_, i) => i !== index);
      }
      // An explicit quantity edit makes the row authoritative — in-flight
      // adds must not roll back over the user's chosen quantity.
      const cur = currentItems[index];
      invalidateKey(makeRowId(cur.product.product_id, cur.selectedSize, cur.selectedOptions, cur.customImageUrl, cur.ccOptions), cutoff);
      const updated = [...currentItems];
      updated[index] = { ...updated[index], quantity };
      return updated;
    });
  };

  const clearCart = () => {
    const cutoff = nextCallIdRef.current;
    setItems(() => {
      invalidateAll(cutoff);
      return [];
    });
  };

  const getCartTotal = () => {
    return items.reduce(
      (total, item) => {
        const price = item.sizePrice || item.product.base_price;
        return total + (price * item.quantity);
      },
      0
    );
  };

  const getCartCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  const isInCart = (productId: number) => {
    return items.some(item => item.product.product_id === productId);
  };

  const replaceCCItem = React.useCallback((rowId: string, updatedItem: CartItem) => {
    const cutoff = nextCallIdRef.current;
    setItems(current => {
      const index = current.findIndex(item => item.rowId === rowId);
      if (index === -1) return current;
      // Any explicit edit makes the row authoritative: pending adds for the
      // old merge key must not roll back (or delete) the edited row, whether
      // or not the edit changed size/options.
      const old = current[index];
      const oldKey = makeRowId(old.product.product_id, old.selectedSize, old.selectedOptions, old.customImageUrl, old.ccOptions);
      invalidateKey(oldKey, cutoff);
      // If the edit makes this row identical to another row (same merge
      // identity as addToCart), merge into that row instead of leaving two
      // duplicates: the target keeps its rowId and gains this row's quantity.
      // The target's key is deliberately not invalidated, so its own
      // in-flight adds still roll back correctly.
      const dupIdx = current.findIndex((item, i) =>
        i !== index &&
        item.product.product_id === updatedItem.product.product_id &&
        item.selectedSize === updatedItem.selectedSize &&
        selectionsKey(item.selectedOptions) === selectionsKey(updatedItem.selectedOptions) &&
        ccKey(item.ccOptions) === ccKey(updatedItem.ccOptions) &&
        sameImage(item.customImageUrl, updatedItem.customImageUrl)
      );
      if (dupIdx > -1) {
        const merged = [...current];
        merged[dupIdx] = { ...merged[dupIdx], quantity: merged[dupIdx].quantity + updatedItem.quantity };
        return merged.filter((_, i) => i !== index);
      }
      const updated = [...current];
      // Force the row's original rowId to persist regardless of what the
      // caller's updatedItem carries, so identity can never drift out from
      // under an in-flight action (e.g. size edit changing selectedSize
      // must not change which row a concurrently-scheduled swipe-delete
      // resolves to).
      updated[index] = { ...updatedItem, rowId };
      return updated;
    });
  }, [invalidateKey]);

  const removeCCItem = React.useCallback((rowId: string) => {
    const cutoff = nextCallIdRef.current;
    setItems(prev => prev.filter(item => {
      if (item.rowId !== rowId) return true;
      invalidateKey(makeRowId(item.product.product_id, item.selectedSize, item.selectedOptions, item.customImageUrl, item.ccOptions), cutoff);
      return false;
    }));
  }, [invalidateKey]);

  // Product snapshots in persisted rows can predate the option flags (or an
  // admin may attach a required group later). Merge only the two flags; never
  // touch rowId/size/price/qty/ccOptions, never set syncError/cartWarning.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const refreshedProductIdsRef = useRef(new Set<number>());
  const isProviderMountedRef = useRef(true);
  useEffect(() => {
    isProviderMountedRef.current = true;
    return () => { isProviderMountedRef.current = false; };
  }, []);

  const fetchAndMergeFlags = React.useCallback(async (force: boolean) => {
    const ids = Array.from(new Set(itemsRef.current.map(i => i.product?.product_id)))
      .filter((id): id is number => typeof id === 'number' && (force || !refreshedProductIdsRef.current.has(id)));
    ids.forEach(id => refreshedProductIdsRef.current.add(id));
    await Promise.all(ids.map(async id => {
      try {
        const fresh = await productsAPI.getProductDetail(id);
        if (!isProviderMountedRef.current) return;
        const selectable = fresh.has_selectable_options;
        const required = fresh.has_required_options;
        setItems(cur => cur.map(it =>
          it.product?.product_id === id
            ? { ...it, product: { ...it.product, has_selectable_options: selectable, has_required_options: required } }
            : it
        ));
      } catch {
        // Best effort: the checkout 400 branch is the fallback.
      }
    }));
  }, []);

  const refreshCartProductFlags = React.useCallback(() => fetchAndMergeFlags(true), [fetchAndMergeFlags]);

  // On mount and whenever a new product_id enters the cart.
  const productIdsKey = items.map(i => i.product?.product_id).join(',');
  useEffect(() => {
    fetchAndMergeFlags(false);
  }, [productIdsKey, fetchAndMergeFlags]);

  // ── BYO Cart state ──
  const [byoItems, setByoItems] = useState<BYOCartItem[]>(() => loadBYOCartFromStorage());

  useEffect(() => {
    saveBYOCartToStorage(byoItems);
  }, [byoItems]);

  const addBYOItem = React.useCallback((item: BYOCartItem) => {
    setByoItems(current => {
      const existingIdx = current.findIndex(i => i.byoId === item.byoId);
      if (existingIdx > -1) {
        const updated = [...current];
        updated[existingIdx] = item;
        return updated;
      }
      return [...current, item];
    });
  }, []);

  const removeBYOItem = React.useCallback((byoId: string) => {
    setByoItems(current => current.filter(i => i.byoId !== byoId));
  }, []);

  const clearBYOItems = React.useCallback(() => {
    setByoItems([]);
  }, []);

  const getByoTotal = React.useCallback(() => {
    return byoItems.reduce((total, item) => total + item.totalPrice * item.quantity, 0);
  }, [byoItems]);

  const ccValue: CCCartContextType = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    isInCart,
    isLoading,
    sessionId,
    cartWarning,
    dismissCartWarning,
    isCartOpen,
    openCart,
    closeCart,
    replaceCCItem,
    removeCCItem,
    refreshCartProductFlags,
    byoItems,
    addBYOItem,
    removeBYOItem,
    clearBYOItems,
    getByoTotal,
  };

  // ── QB Cart state ──
  const [qbItems, setQBItems] = useState<QBCartItem[]>(() => loadQBCartFromStorage());

  useEffect(() => {
    saveQBCartToStorage(qbItems);
  }, [qbItems]);

  const addQBItem = React.useCallback((item: Omit<QBCartItem, 'quantity'> & { quantity?: number }) => {
    const qty = item.quantity ?? 1;
    setQBItems(current => {
      const idx = current.findIndex(
        i => i.itemId === item.itemId && i.type === item.type &&
             JSON.stringify(i.options) === JSON.stringify(item.options)
      );
      if (idx > -1) {
        const updated = [...current];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + qty };
        return updated;
      }
      return [...current, { ...item, quantity: qty }];
    });
  }, []);

  const removeQBItem = React.useCallback((itemId: number, type: QBCartItem['type']) => {
    setQBItems(current => current.filter(i => !(i.itemId === itemId && i.type === type)));
  }, []);

  const updateQBQuantity = React.useCallback((itemId: number, type: QBCartItem['type'], quantity: number) => {
    if (quantity <= 0) {
      removeQBItem(itemId, type);
      return;
    }
    setQBItems(current =>
      current.map(i =>
        i.itemId === itemId && i.type === type ? { ...i, quantity } : i
      )
    );
  }, [removeQBItem]);

  const clearQBCart = React.useCallback(() => {
    setQBItems([]);
  }, []);

  const getQBCartTotal = React.useCallback(() => {
    return qbItems.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [qbItems]);

  const replaceQBItemAtIndex = React.useCallback((index: number, newItem: QBCartItem) => {
    setQBItems(current => {
      if (index < 0 || index >= current.length) return current;
      const replaced = [...current];
      replaced[index] = newItem;
      // Check all other positions for an identical item (same itemId, type, options)
      const dupIdx = replaced.findIndex(
        (i, i_idx) =>
          i_idx !== index &&
          i.itemId === newItem.itemId &&
          i.type === newItem.type &&
          JSON.stringify(i.options) === JSON.stringify(newItem.options)
      );
      if (dupIdx > -1) {
        // Sum quantities and remove the duplicate
        const mergedQty = replaced[index].quantity + replaced[dupIdx].quantity;
        replaced[index] = { ...replaced[index], quantity: mergedQty };
        replaced.splice(dupIdx, 1);
      }
      return replaced;
    });
  }, []);

  const qbCartCount = qbItems.reduce((count, item) => count + item.quantity, 0);

  const qbValue: QBCartContextType = {
    qbItems,
    addQBItem,
    removeQBItem,
    updateQBQuantity,
    clearQBCart,
    getQBCartTotal,
    qbCartCount,
    replaceQBItemAtIndex,
  };

  return (
    <CCCartContext.Provider value={ccValue}>
      <QBCartContext.Provider value={qbValue}>
        {children}
      </QBCartContext.Provider>
    </CCCartContext.Provider>
  );
};
